import { useCallback, useMemo, useState } from "react";
// import styles from "./index.module.scss";
import { ModalBase } from "../ModalBase";
import { Wizard } from "components/wizards/Wizard";
import { setActiveModal } from "store/modal/slice";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { SelectDestinationStep } from "./steps/SelectDestination";
import { PlatformSettings } from "./steps/PlatformSettings";
import { LivestreamDetailsStep } from "./steps/LivestreamDetails";
import { ProcessingStep } from "./steps/ProcessingStep";
import { PlatformId } from "views/page-content/platforms/types";
import { RootState } from "store/reducers";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import {
    updateGeneralForm,
    updateVideoPlayerForm
} from "store/platforms/slice";
import {
    GeneralPlatformForm,
    QualitySelection,
    VideoPlayerForm
} from "store/platforms/types";
import { useSpeedTest } from "hooks/useSpeedTest";
import { FacebookEdge } from "api/facebook/types";

export enum NewLivestreamModalSteps {
    SelectDestination = "select-destination",
    PlatformSettings = "platform-settings",
    LivestreamDetails = "livestream-details",
    Processing = "processing"
}

export const NewLivestreamModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { general, videoplayer } = useSelector(
        (state: RootState) => state.platforms
    );

    const [activeStep, setActiveStep] = useState<NewLivestreamModalSteps>(
        NewLivestreamModalSteps.SelectDestination
    );

    const [selectedPlatform, setSelectedPlatform] = useState<PlatformId | null>(
        null
    );

    const [selectedDestinationId, setSelectedDestinationId] = useState<
        string | null
    >(null);

    const [currentFacebookEdge, setCurrentFacebookEdge] =
        useState<FacebookEdge>(null);

    const [onAdvanceStep, setOnAdvanceStep] = useState<(() => void) | null>(
        null
    );
    const [onDismiss, setOnDismiss] = useState<(() => void) | null>(null);

    const clearCallbacks = useCallback(() => {
        setOnAdvanceStep(null);
        setOnDismiss(null);
    }, []);

    const [
        hasAddedPlatformSpecificSettings,
        setHasAddedPlatformSpecificSettings
    ] = useState<boolean>(false);

    const canSubmit = useMemo(() => {
        return true;
    }, []);

    const getPlatformSubsettingsModalHeader = useCallback(
        (platformId: PlatformId | null) => {
            if (!platformId) return null;

            switch (platformId) {
                case PlatformId.Youtube:
                    return t("platforms:channel-settings");
                case PlatformId.Twitch:
                    return t("platforms:twitch");
                case PlatformId.Facebook:
                    switch (currentFacebookEdge) {
                        case "accounts":
                            return t("facebook-destination:page-settings");
                        case "me":
                            return t("facebook-destination:timeline-settings");
                        default:
                            return t(`platforms:facebook`);
                    }
                default:
                    return t(`platforms:${selectedPlatform}`);
            }
        },
        [t, selectedPlatform, currentFacebookEdge]
    );

    const advanceStep = useCallback(() => {
        if (onAdvanceStep) onAdvanceStep();

        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                setActiveStep(NewLivestreamModalSteps.LivestreamDetails);
                break;
            case NewLivestreamModalSteps.PlatformSettings:
                setActiveStep(NewLivestreamModalSteps.SelectDestination);
                setHasAddedPlatformSpecificSettings(false);
                setSelectedDestinationId(null);

                break;
            case NewLivestreamModalSteps.LivestreamDetails:
                setActiveStep(NewLivestreamModalSteps.Processing);
                break;
            case NewLivestreamModalSteps.Processing:
                if (canSubmit) {
                    // TODO: reset form state
                    setActiveStep(NewLivestreamModalSteps.SelectDestination);
                } else {
                    setActiveStep(NewLivestreamModalSteps.LivestreamDetails);
                }
                break;
        }

        clearCallbacks();
    }, [activeStep, canSubmit, onAdvanceStep, clearCallbacks]);

    const advanceButtonText = useMemo(() => {
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                return t("misc:next");
            case NewLivestreamModalSteps.PlatformSettings:
                return t("buttons:save");
            case NewLivestreamModalSteps.LivestreamDetails:
                return t("buttons:schedule-event");
            case NewLivestreamModalSteps.Processing:
                if (canSubmit) {
                    return t("buttons:schedule-another");
                } else {
                    return t("buttons:back");
                }
            default:
                return t("buttons:back");
        }
    }, [activeStep, t, canSubmit]);

    const dismissButtonText = useMemo(() => {
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                return t("buttons:cancel");
            case NewLivestreamModalSteps.PlatformSettings:
                return hasAddedPlatformSpecificSettings
                    ? t("buttons:back")
                    : t("buttons:cancel");
            case NewLivestreamModalSteps.LivestreamDetails:
                return t("buttons:back");
            case NewLivestreamModalSteps.Processing:
                if (canSubmit) {
                    return t("buttons:done");
                } else {
                    return t("buttons:cancel");
                }
            default:
                return t("buttons:cancel");
        }
    }, [t, activeStep, canSubmit, hasAddedPlatformSpecificSettings]);

    const dismissButtonAction = useCallback(() => {
        if (onDismiss) onDismiss();
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                dispatch(setActiveModal(null));
                break;
            case NewLivestreamModalSteps.PlatformSettings:
                if (!hasAddedPlatformSpecificSettings)
                    setActiveStep(NewLivestreamModalSteps.SelectDestination);
                setHasAddedPlatformSpecificSettings(false);
                setSelectedDestinationId(null);
                setCurrentFacebookEdge(null);
                break;
            case NewLivestreamModalSteps.LivestreamDetails:
                setActiveStep(NewLivestreamModalSteps.SelectDestination);
                break;
            case NewLivestreamModalSteps.Processing:
                dispatch(setActiveModal(null));
                setActiveStep(NewLivestreamModalSteps.SelectDestination);
                break;
        }
        clearCallbacks();
    }, [
        activeStep,
        dispatch,
        hasAddedPlatformSpecificSettings,
        onDismiss,
        clearCallbacks
    ]);

    const canProceedToNextStep = useMemo(() => {
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                const hasAddedExternalPlatforms =
                    general.generalForm.selectedSimulcastDestinations.length >
                    0;

                const hasAddedCollections =
                    videoplayer?.videoPlayerForm?.playlistIds.length > 0;

                return (
                    hasAddedExternalPlatforms ||
                    hasAddedCollections ||
                    general.generalForm?.showInCatalog
                );
            case NewLivestreamModalSteps.PlatformSettings:
                return hasAddedPlatformSpecificSettings;
            case NewLivestreamModalSteps.LivestreamDetails:
                return true; // Add actual validation logic here
            case NewLivestreamModalSteps.Processing:
                return canSubmit; // Add actual validation logic here
            default:
                return false;
        }
    }, [
        activeStep,
        canSubmit,
        general,
        hasAddedPlatformSpecificSettings,
        videoplayer
    ]);

    const onGeneralFormChange = useCallback(
        (update: Partial<GeneralPlatformForm>) => {
            dispatch(updateGeneralForm(update));
        },
        [dispatch]
    );

    const onVideoPlayerFormChange = useCallback(
        (update: Partial<VideoPlayerForm>) => {
            dispatch(updateVideoPlayerForm(update));
        },
        [dispatch]
    );

    const handleSetStreamQuality = useCallback(
        (quality: QualitySelection) =>
            onGeneralFormChange({ selectedQuality: quality }),
        [onGeneralFormChange]
    );

    const { speedTestStatus, results, runSpeedTest } = useSpeedTest({
        onCompleted: (results) => {
            handleSetStreamQuality(results?.suggestedSetting);
        }
    });

    const modalHeaderText = useMemo(() => {
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                return t("platforms:new-livestream");
            case NewLivestreamModalSteps.PlatformSettings:
                return hasAddedPlatformSpecificSettings
                    ? getPlatformSubsettingsModalHeader(selectedPlatform)
                    : t(`platforms:${selectedPlatform}`);
            case NewLivestreamModalSteps.LivestreamDetails:
            case NewLivestreamModalSteps.Processing:
            default:
                return t("platforms:new-livestream");
        }
    }, [
        activeStep,
        t,
        selectedPlatform,
        hasAddedPlatformSpecificSettings,
        getPlatformSubsettingsModalHeader
    ]);

    return (
        <ModalBase
            isOpen
            header={modalHeaderText}
            successButton={advanceButtonText}
            onSuccess={advanceStep}
            dismissButton={dismissButtonText}
            onDismiss={dismissButtonAction}
            successDisabled={!canProceedToNextStep}
            shouldNotCloseOnSuccess={true}
        >
            <div>
                <Wizard<NewLivestreamModalSteps>
                    activeStep={activeStep}
                    steps={[
                        {
                            id: NewLivestreamModalSteps.SelectDestination,
                            component: (
                                <SelectDestinationStep
                                    setSelectedPlatform={setSelectedPlatform}
                                    advanceToPlatformSettings={(
                                        destination?: DestinationDescriptor
                                    ) => {
                                        if (destination) {
                                            setSelectedDestinationId(
                                                destination.id
                                            );
                                            setHasAddedPlatformSpecificSettings(
                                                true
                                            );
                                        }
                                        setActiveStep(
                                            NewLivestreamModalSteps.PlatformSettings
                                        );
                                    }}
                                />
                            )
                        },
                        {
                            id: NewLivestreamModalSteps.PlatformSettings,
                            component: (
                                <PlatformSettings
                                    selectedPlatform={selectedPlatform}
                                    setHasAddedPlatformSpecificSettings={
                                        setHasAddedPlatformSpecificSettings
                                    }
                                    setOnAdvanceStep={setOnAdvanceStep}
                                    selectedDestinationId={
                                        selectedDestinationId
                                    }
                                    advanceStep={advanceStep}
                                    setOnDismiss={setOnDismiss}
                                    setCurrentFacebookEdge={
                                        setCurrentFacebookEdge
                                    }
                                />
                            )
                        },
                        {
                            id: NewLivestreamModalSteps.LivestreamDetails,
                            component: (
                                <LivestreamDetailsStep
                                    speedTestStatus={speedTestStatus}
                                    speedTestResults={results}
                                    handleRetrySpeedTest={runSpeedTest}
                                    onGeneralFormChange={onGeneralFormChange}
                                    onVideoPlayerFormChange={
                                        onVideoPlayerFormChange
                                    }
                                    setStreamQuality={handleSetStreamQuality}
                                    minuteOffset={
                                        selectedPlatform === "youtube"
                                            ? 20
                                            : undefined
                                    }
                                />
                            )
                        },
                        {
                            id: NewLivestreamModalSteps.Processing,
                            component: <ProcessingStep />
                        }
                    ]}
                />
            </div>
        </ModalBase>
    );
};
